/* the wholesale product index and its public, search only key */
const ALGOLIA_APP_ID = "M00M3RFW1A"
const ALGOLIA_SEARCH_KEY = "73a374afec1531d4ea65286681ba81a6"
const SEARCH_INDEX = "wholesale.search.products"

class ProductSearchResult {
  page = 0
  pageCount = 0
  hitCount = 0
  special = []
  categories = []
  hits = []
  retiredCount = 0
}

class ProductSearch extends EventTarget {
  static SORT_FEATURED = "featured"

  baseIndexName = SEARCH_INDEX
  algolia = algoliasearch.algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
  requestCount = 0

  page = 1
  pageSize = 48
  query = ""
  sort = ProductSearch.SORT_FEATURED
  // the collection every result must belong to, kept apart from `collection` so a department can be chosen within it
  scope
  // the facet the scope names: a collection by default, or hierarchy.special for a view like Popular
  scopeFacet = "hierarchy.lvl0"
  collection
  special
  category
  subCategory
  vendor
  inStockOnly
  includeRetired
  // across the catalogue: the hierarchy is every collection with its categories, unmoved by the collection and category chosen
  staticHierarchy = false

  static STATE_FIELDS = ["scope", "collection", "special", "category", "subCategory", "vendor", "inStockOnly", "includeRetired", "query", "sort", "page"]

  constructor() {
    super()
  }

  // the filters and position as a plain object, so a view can be stashed and put back
  get state() {
    return Object.fromEntries(ProductSearch.STATE_FIELDS.map(f => [f, this[f]]))
  }

  set state(state) {
    for(let field of ProductSearch.STATE_FIELDS) {
      if(field in state) this[field] = state[field]
    }
  }

  setSpecial(special) {
    this.special = special ? special : "All"
    this.category = null
    this.subCategory = null
    this.page = 1
    this.search()
  }

  setCategory(category) {
    this.category = category
    this.subCategory = null
    this.page = 1
    this.search()
  }

  setSubCategory(subCategory, category) {
    this.category = category
    this.subCategory = subCategory
    this.page = 1
    this.search()
  }

  search() {
    var requestId = ++this.requestCount

    let indexName = this.baseIndexName
    if(this.sort != ProductSearch.SORT_FEATURED) {
      indexName = `${indexName}.sort-${this.sort}`
    }

    let filters = []
    let facetFilters = []

    let scopeFilter = this.scope ? [`${this.scopeFacet}:${this.scope}`] : []
    filters.push(...scopeFilter)

    if(this.collection) {
      filters.push(`hierarchy.lvl0:${this.collection}`)
    }

    let stockFilter = this.inStockOnly ? ["offer.in_stock:true"] : []
    filters.push(...stockFilter)

    // the retired filter is applied to the hits but kept out of the retired count query
    let retiredFilter = this.includeRetired ? [] : ["offer.retired:false"]

    // the view counts reflect the collection and stock filters but not the view itself; with a static hierarchy only the scope and stock filters
    var viewFilters = this.staticHierarchy ? [...scopeFilter, ...stockFilter, ...retiredFilter] : [...filters, ...retiredFilter]

    if(this.special) {
      filters.push(`hierarchy.special:${ this.special }`)
    }

    if(this.subCategory) {
      facetFilters.push(`hierarchy.lvl2:${this.collection} -> ${ this.category } -> ${ this.subCategory }`)
    } else if(this.category) {
      facetFilters.push(`hierarchy.lvl1:${this.collection} -> ${ this.category }`)
    }

    if(this.vendor) {
      filters.push(`product.vendor:${this.vendor}`)
    }

    let queries = [
      { // query to get hits based on the users current search and filters
        indexName: indexName, query: this.query,
        params: {
          facetFilters: [...filters, ...retiredFilter, ...facetFilters], hitsPerPage: this.pageSize, page: this.page - 1,
          attributesToRetrieve: ["id", "title", "sku", "offer", "product.id", "product.variant_count", "product.handle", "product.title", "product.teaser", "product.vendor", "tags", "image", "reviews", "offer", "tiers", "hierarchy.special", "restrictions"]
      }},
      { // query to get category facets: the collection's categories and sub categories, or the collections themselves with their categories
        indexName: indexName, query: this.query,
        params: {
          facetFilters: this.staticHierarchy ? [...scopeFilter, ...stockFilter, ...retiredFilter] : [...filters, ...retiredFilter], hitsPerPage: 0, maxValuesPerFacet: 1000, sortFacetValuesBy: "alpha",
          facets: this.collection && !this.staticHierarchy ? ["hierarchy.lvl1", "hierarchy.lvl2"] : ["hierarchy.lvl0", "hierarchy.lvl1"]
      }},
      { // query to get special facets
        indexName: indexName, query: this.query,
        params: {
          facetFilters: viewFilters, hitsPerPage: 0, maxValuesPerFacet: 1000, sortFacetValuesBy: "alpha",
          facets: ["hierarchy.special"]
      }},
      { // query to count retired products at the current position, whether or not they are shown
        indexName: indexName, query: this.query,
        params: {
          facetFilters: [...filters, ...facetFilters], hitsPerPage: 0,
          facets: ["offer.retired"]
      }}
    ]

    this.algolia.search(queries).then(({results}) => {

      // a more recent search has been done so discard these results
      if(requestId != this.requestCount) {return}

      let result = new ProductSearchResult()
      result.hits = results[0].hits
      result.hitCount = results[0].nbHits
      result.page = results[0].page
      result.pageCount = results[0].nbPages

      // build the categories parent/child hierarchy

      // first create the categories
      result.categories = []
      if(results[1].facets["hierarchy.lvl0"]) {
        for(let [collectionName, value] of Object.entries(results[1].facets["hierarchy.lvl0"])) {
          result.categories.push({name: collectionName, count: value, children: []})
        }
        result.categories.sort((a, b) => b.count - a.count)
      }
      // within a collection its categories are the top level; across the catalogue they sit under their collections
      if(results[1].facets["hierarchy.lvl1"]) {
        for(let [key, value] of Object.entries(results[1].facets["hierarchy.lvl1"])) {
          let [collectionName, categoryName] = key.split(" -> ")
          if(this.collection && !this.staticHierarchy) {
            if(collectionName == this.collection) {
              result.categories.push({
                name: categoryName,
                count: value,
                children: []
              })
            }
          } else {
            let collection = result.categories.find(c => c.name == collectionName)
            if(collection) {
              collection.children.push({name: categoryName, count: value})
            }
          }
        }
      }

      // then add the sub categories to each category
      if(results[1].facets["hierarchy.lvl2"]) {
        for(let [key, value] of Object.entries(results[1].facets["hierarchy.lvl2"])) {
          let [collectionName, categoryName, subCategoryName] = key.split(" -> ")
          if(collectionName == this.collection) {
            let category = result.categories.find(c => c.name == categoryName)
            if(category) {
              category.children.push({name: subCategoryName, count: value})
            }
          }
        }
      }

      // "Other" holds what no category claims, so it reads last wherever it appears
      const last = (a, b) => (a.name == "Other") - (b.name == "Other")
      result.categories.sort(last)
      for(let category of result.categories) category.children.sort(last)

      // add special filters
      result.special = []
      if(results[2].facets["hierarchy.special"]) {
        for(let [key, value] of Object.entries(results[2].facets["hierarchy.special"])) {
          result.special.push({
            name: key,
            count: value,
            children: []
          })
        }
      }

      result.retiredCount = (results[3].facets["offer.retired"] || {})["true"] || 0

      this.dispatchEvent(new CustomEvent("results", {detail: result}))
    })
  }
}


// the row inside a filter. `href` makes it a link, so the filter can be opened in a new tab,
// reached by keyboard and followed by a crawler; without one it stays a plain block
const createFilterRowHtml = (href, name, count) => href
  ? `<a href="${href}"><span>${ name }</span><small>${ count }</small></a>`
  : `<div><span>${ name }</span><small>${ count }</small></div>`

// `href` is called with a filter's name, and its parent's for a sub category, and returns the address it leads to
createSpecialFiltersHtml = (result, search, href = () => null) => {
  let specialFiltersHtml = ""
  for(let special of result.special) {
    let isSelected = special.name == search.special
    let isDimmed = !isSelected
    specialFiltersHtml += `<li data-name="${special.name}" class="${isDimmed ? "dimmed" : ""} ${isSelected ? "selected" : ""}">${createFilterRowHtml(href(special.name), special.name, special.count)}</li>`
  }
  return specialFiltersHtml
}

// `selected` names the chosen category and sub category; `opened` the categories whose children are listed
createCategoryFiltersHtml = (result, selected, opened = [], href = () => null) => {
  let categoryFiltersHtml = ""
  for(let category of result.categories) {
    let isCategorySelected = category.name == selected.category
    let isCategoryDimmed = selected.category && !isCategorySelected
    let subCategoryFiltersHtml = ""
    if(category.name == selected.category || opened.includes(category.name)) {
      subCategoryFiltersHtml += "<ul>"
      for(let subCategory of category.children) {
        let isSubCategorySelected = subCategory.name == selected.subCategory && category.name == selected.category
        // sub categories sit knocked back until one of them is chosen
        let isSubCategoryDimmed = !isSubCategorySelected
        subCategoryFiltersHtml += `<li data-name="${subCategory.name}" data-parent="${category.name}" class="${isSubCategoryDimmed ? "dimmed" : ""} ${isSubCategorySelected ? "selected" : ""}">${createFilterRowHtml(href(subCategory.name, category.name), subCategory.name, subCategory.count)}</li>`
      }
      subCategoryFiltersHtml += "</ul>"
    }

    categoryFiltersHtml += `<li data-name="${category.name}" class="${isCategoryDimmed ? "dimmed" : ""} ${isCategorySelected ? "selected" : ""}">${createFilterRowHtml(href(category.name), category.name, category.count)}${subCategoryFiltersHtml}</li>`
  }

  return categoryFiltersHtml
}

/*
  the price breaks a hit offers the customer, lowest quantity first: their tier's table in their
  market, or the market's own price alone for an account with no tier yet. Null for a visitor, who
  sees no prices, and where the index holds no price for the market, since the shop price is in
  another currency
*/
priceBreaks = (hit) => {
  if(!window.loggedIn) return null
  let market = window.market?.handle
  let tiers = hit.tiers?.[market]?.[window.pricingTier]
  if(tiers) return tiers
  let price = hit.offer.prices?.[market]
  return price ? [{quantity: 1, price: price.price}] : null
}

/* the index records for a list of variant ids, keyed by id; a variant not in the index is left out */
variantRecords = async (ids) => {
  let client = algoliasearch.algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
  let {results} = await client.getObjects({requests: ids.map(id => ({indexName: SEARCH_INDEX, objectID: String(id)}))})
  return Object.fromEntries(results.filter(Boolean).map(record => [record.id, record]))
}

/* the index records whose SKUs are listed, keyed by upper case SKU. sku is searchable but not a facet,
   so each SKU is a query restricted to it, and only an exact match is kept */
skuRecords = async (skus) => {
  let client = algoliasearch.algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
  let records = {}
  for(let i = 0; i < skus.length; i += 50) {
    let batch = skus.slice(i, i + 50)
    let {results} = await client.search(batch.map(sku => ({indexName: SEARCH_INDEX, query: sku, params: {restrictSearchableAttributes: ["sku"], typoTolerance: false, hitsPerPage: 20}})))
    results.forEach((result, j) => {
      let hit = result.hits.find(hit => (hit.sku || "").toUpperCase() == batch[j].toUpperCase())
      if(hit) records[batch[j].toUpperCase()] = hit
    })
  }
  return records
}

/* what one unit costs at a quantity, from a list of price breaks */
breakPrice = (breaks, quantity) => breaks.filter(b => quantity >= b.quantity).pop()?.price ?? breaks[0].price

/* every line that can still be ordered, for the pricebook: only the fields it needs, a page of 1,000 at a time.
   An index that stops short of them all (its paginationLimitedTo below the count) is an error, not a short pricebook */
pricebookRecords = async () => {
  let client = algoliasearch.algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
  let records = [], page = 0, pages = 1, total = 0
  while(page < pages) {
    let {results: [result]} = await client.search([{indexName: SEARCH_INDEX, query: "", params: {
      filters: "offer.retired:false", hitsPerPage: 1000, page, typoTolerance: false, attributesToHighlight: [], attributesToSnippet: [],
      attributesToRetrieve: ["sku", "barcode", "weight", "title", "product.title", "product.vendor", "product.variant_count", "offer.prices", "offer.backorder", "tiers", "customs", "restrictions"],
    }}])
    records.push(...result.hits)
    pages = result.nbPages
    total = result.nbHits
    page++
  }
  if(records.length < total) throw new Error(`The index returned ${records.length.toLocaleString()} of ${total.toLocaleString()} lines`)
  return records
}

/* the pricebook as a CSV: every line that can be ordered, its details, any limits on its sale or shipping, and its
   price at each of the customer's breaks in the market's currency, ex VAT. A line already at its floor has one
   price, repeated across the breaks */
downloadPricebook = async () => {
  let market = window.market.handle
  let records = await pricebookRecords()
  let tierOf = (record) => window.pricingTier && record.tiers?.[market]?.[window.pricingTier]
  let quantities = [...new Set(records.flatMap(record => (tierOf(record) || []).map(tier => tier.quantity)))].sort((a, b) => a - b)
  let currency = records.find(record => record.offer.prices?.[market])?.offer.prices[market].currency || ""
  let names = new Intl.DisplayNames(["en"], {type: "region"})
  const country = (code) => { try { return code ? `${names.of(code)} (${code})` : "" } catch(e) { return code } }
  const money = (value) => value == null ? "" : value.toFixed(2)
  const notes = (restrictions) => [
    restrictions?.battery && "Bare battery cells: UK/Europe shipping only",
    restrictions?.ups_only && "UPS shipping only",
    restrictions?.max_per_customer && `Limited to ${restrictions.max_per_customer} per customer`,
  ].filter(Boolean).join("; ")

  let rows = [["Brand", "SKU", "Product", "Variant", "Barcode", "HS tariff code", "Country of origin", "Weight (g)", "Backorder", "Notes", `Retail price (${currency}, ex VAT)`, ...quantities.map(q => `${q}+ (${currency}, ex VAT)`)]]
  // Pimoroni's own lines first, then two blank rows, then everyone else's by brand; each by SKU, PIM99 before PIM100
  const brand = (record) => record.product.vendor || "Generic"
  const bySku = (a, b) => (a.sku || "").localeCompare(b.sku || "", "en", {numeric: true})
  const row = (record) => {
    let tiers = tierOf(record)
    return [
      brand(record), record.sku, record.product.title, record.product.variant_count > 1 ? record.title : "",
      record.barcode, record.customs?.tariff_code, country(record.customs?.country_of_origin), record.weight,
      record.offer.backorder ? "Allowed" : "Not allowed", notes(record.restrictions), money(record.offer.prices[market].price),
      ...quantities.map(q => tiers ? money(breakPrice(tiers, q)) : ""),
    ]
  }
  let priced = records.filter(record => record.offer.prices?.[market])
  let ours = priced.filter(record => brand(record) == "Pimoroni").sort(bySku)
  let theirs = priced.filter(record => brand(record) != "Pimoroni").sort((a, b) => brand(a).localeCompare(brand(b)) || bySku(a, b))
  rows.push(...ours.map(row))
  if(ours.length && theirs.length) rows.push([], [])
  rows.push(...theirs.map(row))

  const cell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`
  let link = document.createElement("a")
  link.href = URL.createObjectURL(new Blob([rows.map(row => row.map(cell).join(",")).join("\r\n")], {type: "text/csv"}))
  link.download = `pimoroni-pricebook-${window.pricingTier || "retail"}-${market}-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  setTimeout(() => URL.revokeObjectURL(link.href), 1000)
}

/* anything marked .download-pricebook downloads it, busy while the index is read */
$(document).on("click", ".download-pricebook", async (e) => {
  e.preventDefault()
  let control = $(e.currentTarget)
  if(control.attr("aria-busy")) return
  control.attr("aria-busy", "true")
  try {
    await downloadPricebook()
  } catch(error) {
    alert(`Your pricebook could not be prepared: ${error.message}. Please try again, or contact us for a copy.`)
  } finally {
    control.removeAttr("aria-busy")
  }
})

createHitHtml = (hit) => {
  let breaks = priceBreaks(hit)
  let best = breaks && breaks.length > 1 ? breaks[breaks.length - 1] : null

  let imageUrl = hit.image || ""
  imageUrl = imageUrl.substring(0, imageUrl.lastIndexOf(".")) + "_300x300" + imageUrl.substring(imageUrl.lastIndexOf("."))

  let isNew = hit.hierarchy.special?.includes("New")
  let isPopular = hit.hierarchy.special?.includes("Popular")
  let isOutOfStock = !hit.offer.in_stock
  let isPreOrder = isOutOfStock && hit.offer.pre_order
  let isRetired = hit.offer.retired
  let stock = hit.offer.in_stock ? `${hit.offer.stock} in stock` : isPreOrder ? "backorder" : "out of stock"

  return `
  <article class="result" data-variant="${hit.id}">
    <a href="${shopRoot}/products/${hit.product.handle}?variant=${hit.id}">
      <div class="image">
        <product-badge class="${isPreOrder ? "_preorder" : ""} ${isOutOfStock ? "_outofstock": ""} ${isPopular ? "_popular": ""} ${isNew ? "_new": ""} ${isRetired ? "_retired": ""}"></product-badge>
        <img src="${imageUrl}" alt="${hit.title}" loading="lazy" />
        ${hit.reviews.count ? `<span class="rating"><star-rating rating="${hit.reviews.average_rating}" rating_count="${hit.reviews.count}"></star-rating></span>` : ""}
      </div>
      <div class="details">
        <h4>
          ${hit.product.title}
          ${hit.title && (hit.product.variant_count > 1) ? `<small>(${hit.title})</small>` : ""}
        </h4>
        <p>${hit.product.teaser}</p>
        <small class="meta"><b>${hit.sku || ""}</b> &middot; ${stock}</small>
      </div>
    </a>
    <footer>
      ${breaks && (!isOutOfStock || isPreOrder) ? createTileCartButtonHtml(isPreOrder ? "Backorder" : "Add") : ""}
      ${breaks ? `<div class="pricing">
        <product-price price="${breaks[0].price}" taxable="false"></product-price>
        ${best ? `<small class="best">${best.quantity}+ at <currency-money amount="${best.price}"></currency-money></small>` : ""}
      </div>` : ""}
    </footer>
  </article>`
}

/* the break quantities of the customer's tier, as the tiered pricing Function has them; one price for an account without a tier */
TIER_BREAKS = {reseller: [1, 5, 10], education: [1, 10], business: [1, 10, 25]}
tierQuantities = () => TIER_BREAKS[window.pricingTier] || [1]

/* a browser row's state at a quantity, as the order page words it: in stock, a backorder, short, out of stock or retired */
rowState = (hit, quantity) => {
  let stock = Math.max(0, hit.offer.stock || 0), wanted = Math.max(1, quantity)
  if(hit.offer.retired) return ["retired", "Retired"]
  if(stock >= wanted) return ["in-stock", `${stock.toLocaleString()} in stock`]
  if(hit.offer.backorder) return ["backorder", `Backorder \u2013 ${stock > 0 ? `${stock.toLocaleString()} in stock` : "none in stock"}`]
  if(stock == 0) return ["out-of-stock", "Out of stock"]
  return ["out-of-stock", `Only ${stock.toLocaleString()} available \u2013 reduce your quantity to ${stock == 1 ? "1" : `${stock.toLocaleString()} or fewer`}`]
}

/* the parts of a browser row that follow its quantity: state, reached break and total */
drawRow = (row, hit, quantity) => {
  let [state, text] = rowState(hit, quantity)
  row.removeClass("in-stock backorder out-of-stock retired").addClass(state)
  row.find(".state").text(text)
  let breaks = priceBreaks(hit), max = hit.restrictions?.max_per_customer || 0
  let reached = breaks && quantity > 0 ? breaks.filter(tier => quantity >= tier.quantity).pop() : null
  row.find(".break").each((i, cell) => {
    let tier = breaks?.find(tier => tier.quantity == parseInt($(cell).attr("data-quantity")) && !(max && tier.quantity > max))
    $(cell).prop("disabled", !tier || hit.offer.retired).toggleClass("reached", !!tier && tier == reached)
    $(cell).find(".price").html(tier ? formatMoney(tier.price, false) : "&ndash;")
  })
  row.find(".total").html(breaks && quantity > 0 ? formatMoney(breakPrice(breaks, quantity) * quantity, false) : "")
  // nothing to order: retired, or out of stock with none already in the order
  row.find(".quantity").prop("disabled", hit.offer.retired || (state == "out-of-stock" && hit.offer.stock <= 0 && quantity == 0))
}

/* the headings over the browser's rows, the breaks stacked under one; a visitor's one heading spans the login prompt */
createRowHeadingsHtml = () => window.loggedIn
  ? `<li class="headings" aria-hidden="true"><span></span><span>Item</span><span class="break-heading">${tierQuantities().length == 1 ? "Price" : "Price breaks"}</span><span>Quantity</span><span class="right">Total</span></li>`
  : `<li class="headings" aria-hidden="true"><span></span><span>Item</span><span class="login-heading">Trade pricing</span></li>`

/* a hit as a row, laid out as the order page's lines: its quantity is the quantity in the order, and editing it edits
   the order (orderRows). A note, such as when it was last ordered, follows the stock state. A visitor sees no prices
   or stock levels and has no quantity to set */
createHitRowHtml = (hit, note = "") => {
  orderRows.hits[hit.id] = hit
  let quantity = orderRows.ordered[hit.id] || 0
  let url = `${shopRoot}/products/${hit.product.handle}?variant=${hit.id}`
  let image = hit.image ? hit.image.replace(/(\.[a-z]+)(\?|$)/i, "_120x120$1$2") : ""
  let restrictions = hit.restrictions || {}
  let max = restrictions.max_per_customer
  let flags = (hit.hierarchy.special?.includes("New") ? `<span class="flag new">New</span>` : "") + (hit.hierarchy.special?.includes("Popular") ? `<span class="flag popular">Popular</span>` : "")
  let rating = hit.reviews?.count && hit.reviews.average_rating ? `<span class="rating">&#9733; ${hit.reviews.average_rating.toFixed(1)} (${hit.reviews.count.toLocaleString()})</span>` : ""
  let row = $(`
  <li class="line" data-variant="${hit.id}">
    <a class="photo" href="${url}">${image ? `<img src="${image}" width="60" height="60" alt="" loading="lazy" />` : ""}</a>
    <div class="info">
      <a class="name" href="${url}">${hit.product.title}${hit.title && hit.product.variant_count > 1 ? ` <small>${hit.title}</small>` : ""}</a><span class="sku">${hit.sku || ""}</span>${flags}
      ${hit.product.teaser ? `<p class="teaser">${hit.product.teaser}</p>` : ""}
      <p class="meta">${[
        // a visitor is not shown stock levels
        window.loggedIn ? `<span class="dot"></span><span class="state"></span>` : "",
        rating,
        max ? `<span class="notes limit">limited to ${max} per customer</span>` : "",
        restrictions.battery ? `<span class="notes">UK/Europe shipping only</span>` : "",
        note ? `<span class="history">${note}</span>` : "",
      ].filter(Boolean).join(" &middot; ")}</p>
    </div>
    ${window.loggedIn ? `<div class="pricing">${tierQuantities().map(q => `<button type="button" class="break" data-quantity="${q}" title="Set to ${q}"><span class="at">${q}+ </span><span class="price"></span></button>`).join("")}</div>
    <input class="quantity" type="number" min="0" ${max ? `max="${max}"` : ""} value="${quantity}" aria-label="Quantity of ${$("<div>").text(hit.product.title).html()}" />` : `<a class="login-price" href="${shopRoot}/account/login?return_url=${encodeURIComponent(location.pathname + location.search)}"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M19 10H20C20.5523 10 21 10.4477 21 11V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V11C3 10.4477 3.44772 10 4 10H5V9C5 5.13401 8.13401 2 12 2C15.866 2 19 5.13401 19 9V10ZM5 12V20H19V12H5ZM11 14H13V18H11V14ZM17 10V9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9V10H17Z"></path></svg>Log in for pricing</a>`}
    <span class="total"></span>
  </li>`)
  drawRow(row, hit, quantity)
  return row
}

/*
  the rows on a page edit the order: a quantity typed, stepped or set from a break is drawn at once and saved in the
  background once editing pauses, as on the order page. One for the page, whichever lists its rows are in (the product
  browser, the home page). Quantities are by variant, one line per variant in this store. Anything on the page
  reading or changing the cart awaits window.orderSaves first; the order page picks up a save from cartedited
*/
orderRows = (() => {
  let ordered = {}, hits = {}, pending = {}, saving = null, pause = null
  const rows = () => $(".catalogue .line[data-variant]")
  const showStatus = (state) => $(".order-rows-state").prop("hidden", false).removeClass("saving saved failed").addClass(state)
    .text({saving: "Saving to your order\u2026", saved: "Order updated", failed: "Your order could not be updated"}[state])
  const holding = (cart) => {
    for(let id in ordered) delete ordered[id]
    for(let item of cart.items) ordered[item.variant_id] = (ordered[item.variant_id] || 0) + item.quantity
  }
  // rows not being edited take the order's quantities, as Shopify holds them
  const sync = () => rows().each((i, element) => {
    let row = $(element), id = row.attr("data-variant"), hit = hits[id]
    if(!hit || id in pending) return
    let quantity = ordered[id] || 0
    if(parseInt(row.find(".quantity").val()) != quantity && !row.find(".quantity").is(":focus")) row.find(".quantity").val(quantity)
    drawRow(row, hit, quantity)
  })
  const load = () => $.getJSON(`${shopRoot}/cart.js`).done((cart) => { holding(cart); sync() })

  const save = async () => {
    clearTimeout(pause)
    while(saving) await saving.catch(() => {})
    if(!Object.keys(pending).length) return
    let sent = pending
    pending = {}
    showStatus("saving")
    saving = fetch(`${shopRoot}/cart/update.js`, {method: "POST", headers: {"Content-Type": "application/json", "Accept": "application/json"}, body: JSON.stringify({updates: sent})})
      .then(response => response.ok ? response.json() : Promise.reject(response))
    try {
      holding(await saving)
      sync()
      showStatus(Object.keys(pending).length ? "saving" : "saved")
      $(window).trigger("cartchanged").trigger("cartedited")
    } catch(error) {
      pending = {...sent, ...pending}
      showStatus("failed")
    } finally {
      saving = null
    }
  }

  const change = (row, quantity) => {
    let id = row.attr("data-variant"), hit = hits[id]
    let max = hit.restrictions?.max_per_customer || 0
    quantity = Math.max(0, max ? Math.min(quantity, max) : quantity)
    ordered[id] = pending[id] = quantity
    drawRow(row, hit, quantity)
    showStatus("saving")
    clearTimeout(pause)
    pause = setTimeout(save, 1500)
  }

  $(() => {
    ;(window.orderSaves ||= []).push(save)
    load()
    $(document).on("input", ".catalogue .quantity", (e) => {
      // a field cleared to type a new number is not a request to take the line out
      if(e.currentTarget.value === "") return
      change($(e.currentTarget).closest(".line"), parseInt(e.currentTarget.value) || 0)
    })
    $(document).on("change", ".catalogue .quantity", (e) => { e.currentTarget.value = ordered[$(e.currentTarget).closest(".line").attr("data-variant")] || 0 })
    $(document).on("click", ".catalogue .break", (e) => {
      let row = $(e.currentTarget).closest(".line")
      change(row, parseInt($(e.currentTarget).attr("data-quantity")))
      row.find(".quantity").val(ordered[row.attr("data-variant")])
    })
    // leaving the page takes any unsaved edits with it
    $(window).on("pagehide", () => {
      if(!Object.keys(pending).length) return
      fetch(`${shopRoot}/cart/update.js`, {method: "POST", keepalive: true, headers: {"Content-Type": "application/json"}, body: JSON.stringify({updates: pending})})
    })
    // the quick order tool, or a reorder, changed the order wholesale
    $(window).on("quickorderadded", load)
  })

  return {ordered, hits, load, save}
})()

// the small cart button in a tile's footer, with its added and refused labels
const createTileCartButtonHtml = (label) => `
  <button type="button" class="add" title="Add to your order">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M1.5 2.5H4.8l2.6 13.2h12.4l2.4-9.6H6.3" /><circle cx="8.6" cy="20.6" r="1.9" /><circle cx="18.2" cy="20.6" r="1.9" /></svg>
    <span class="label">${label}</span><span class="done">Added</span><span class="refusal">None left in stock</span>
  </button>`

/* every tile's cart button adds one of its variant */
$(document).on("click", ".result footer .add", (e) => {
  let button = $(e.currentTarget)
  let tile = button.closest(".result")
  $.post(`${shopRoot}/cart/add.js`, {items: [{id: tile.data("variant"), quantity: 1}]}, "json").done(() => {
    button.addClass("added")
    setTimeout(() => button.removeClass("added"), 2000)
    $(window).trigger("cartchanged")
  }).fail(() => {
    button.addClass("refused")
    setTimeout(() => button.removeClass("refused"), 2500)
  })
})

